import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { ReactComponent as CsvIcon } from 'assets/icons/file-csv.svg';
import { ReactComponent as XlsIcon } from 'assets/icons/file-xls.svg';
import { themeOptions } from 'assets/styles/theme';
import { TrackingService } from 'services/trackingService';
import { fetchCsvData } from 'services/fieldNotebookService';
import { ReduxState } from 'store';
import { FormState } from 'store/states/forms/types';
import { HistoricState } from 'store/states/historics/types';
import { OrganizationState } from 'store/states/organization/types';
import { handleDataToGenerateCsv } from 'utils/fieldNotebookUtils/csvUtils';
import { handleDataToGenerateXls } from 'utils/fieldNotebookUtils/xlsUtils';
import { ExportButton } from './styles';

type ExportFormat = 'csv' | 'xls';

interface HistoricExportButtonProps {
  disabled?: boolean;
  loading?: boolean;
}

export function HistoricExportButton({
  disabled,
  loading,
}: HistoricExportButtonProps) {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);
  const isOpen = Boolean(anchorEl);

  const { externalId } = useSelector<ReduxState, OrganizationState>(
    (state) => state.organization,
  );
  const jwt = useSelector<ReduxState, string>((state) => state.user.jwt);
  const { selectedForm } = useSelector<ReduxState, FormState>(
    (state) => state.forms,
  );
  const { filterOptions } = useSelector<ReduxState, HistoricState>(
    (state) => state.historics,
  );

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const runExport = async (format: ExportFormat) => {
    handleClose();
    if (exporting) return;

    TrackingService.watchGenericEvent(
      format === 'csv'
        ? 'historyscreen_click_CSVdownloadbutton'
        : 'historyscreen_click_XLSdownloadbutton',
    );

    setExporting(true);
    try {
      const historics = await fetchCsvData({
        externalId: String(externalId),
        jwt,
        formId: selectedForm.form_id,
        initialDate: filterOptions.initialDate,
        finalDate: filterOptions.finalDate,
        search: filterOptions.search,
      });

      if (format === 'csv') {
        handleDataToGenerateCsv(historics, selectedForm, currentLanguage);
      } else {
        await handleDataToGenerateXls(historics, selectedForm, currentLanguage);
      }
    } catch (err) {
      console.error(`Failed to generate ${format.toUpperCase()}`, err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <ExportButton
        variant="outlined"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        rightIcon={
          <ArrowDropDownIcon
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }}
          />
        }
        onClick={handleOpen}
        disabled={disabled || exporting}
        isLoading={exporting || loading}
      >
        {t('download_as')}
      </ExportButton>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        PaperProps={{ style: { width: anchorEl?.offsetWidth } }}
      >
        <MenuItem onClick={() => runExport('csv')}>
          <ListItemIcon>
            <CsvIcon
              style={{
                width: 20,
                height: 20,
                color: themeOptions.palette.primary.dark,
              }}
            />
          </ListItemIcon>
          <ListItemText>CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => runExport('xls')}>
          <ListItemIcon>
            <XlsIcon
              style={{
                width: 20,
                height: 20,
                color: themeOptions.palette.primary.dark,
              }}
            />
          </ListItemIcon>
          <ListItemText>XLS</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
